/**
 * Compare-2-Areas card.
 *
 * Pure client-side. No API, no backend, no new dataset.
 *
 * Phase-2 refactor (numeric-score removal):
 *   The original card derived a per-facet 0–10 score (price / safety / vibe /
 *   commute) and summed them into a /40 composite. The user judged that
 *   composite untrustworthy — a place could land at "5.6 / 10 — Marginal fit"
 *   while its verdict colour said something stronger or weaker. We removed the
 *   numbers entirely and re-grounded the card on the four signals the user
 *   already trusts: verdict colour, reasons, school presence (categorical),
 *   safety (categorical), and the tradeoff indicator. The card still picks a
 *   side, but the pick is driven by verdict colour with tag-derived tie-breaks
 *   — never by a hidden composite.
 *
 * This is a DECISION layer ON TOP of the existing loop — it does not replace
 * or modify `showVerdict`. The single-pin flow still calls `showVerdict`; the
 * two-pin flow calls `showCompareCard` here.
 */

import type { Verdict } from "../data/verdicts";
import { getSchoolDensity, densityLabel } from "../data/school-density";
import { getSafety, type SafetyLevel } from "../data/safety-signal";
import { getTradeoff, type Tradeoff } from "../data/tradeoff";
import {
  getMode,
  getModeLabel,
  subscribe,
  type LensMode,
  type PersonaMode,
} from "./decision-lens";
import {
  getPriority,
  getPriorityLabel,
  getPriorityAccent,
  subscribePriority,
  type Priority,
} from "./constraint-priority";
import {
  getBudgetBand,
  getBudgetBandLabel,
  getBudgetBandAccent,
  subscribeBudgetBand,
  subscribeBudgetBucket,
} from "./budget-band";
import { classifyFit, fitDecisionAnchor, fitLabel } from "./budget-school-fit";
import {
  constrainBestFor,
  constrainTradeoff,
  hasRailAccess,
} from "./constraint-layer";
import { schoolFactBullet } from "./school-facts";
// `practicalPriceLabel`, `getPurchaseMode`, and `getPropertyType` were
// imported here only to feed the deleted `scorePrice` facet scorer's
// user-facing note. The compare card no longer renders any per-facet
// numeric value, so the practical-price label is gone too — the price
// signal is now carried by the verdict pill + reasons + the "Best for"
// audience line, never by a side-by-side price string.
import { deriveCityFromVerdict } from "./why-this-beats-that";
import { MODE_ACCENT } from "./tokens";
import { PRICING_REFRESHED_AT } from "../data/suburb-pricing";

const CARD_ID = "hyperlocal-compare-card";

/**
 * Per-mode accent so the compare card's header badge + final-block border
 * shift colour with the lens — matches verdict-card so the two surfaces
 * always read as driven by the same global mode. Badge hues come from
 * tokens.MODE_ACCENT (the "bachelor" persona renders with the renter
 * accent); only the contrast inks for text-on-badge live here.
 */
const PERSONA_ACCENT: Record<PersonaMode, { badge: string; badgeFg: string }> = {
  family:   { badge: MODE_ACCENT.family,   badgeFg: "#0b1a0e" },
  bachelor: { badge: MODE_ACCENT.renter,   badgeFg: "#0b1420" },
  investor: { badge: MODE_ACCENT.investor, badgeFg: "#1a1200" },
};

/** Unsubscribe handle for the open compare card. */
let activeCompareUnsubscribe: (() => void) | null = null;

/** Same lifecycle handle for the priority subscription — both must release on
 * card dismiss so the module never leaks listeners for closed cards. */
let activeComparePriorityUnsubscribe: (() => void) | null = null;

/** Budget-band unsubscribe handle — parallel to priority. Under School
 * priority, flipping the band must re-render the card so the Good-fit /
 * Stretch / Weak-fit copy, fit chip, and decision anchor all update. */
let activeCompareBandUnsubscribe: (() => void) | null = null;

/** Bucket-level unsubscribe — catches within-band moves (e.g. A$600–700k
 *  → A$700–800k) where the band-level subscriber above no-ops. */
let activeCompareBucketUnsubscribe: (() => void) | null = null;

/**
 * Release every compare-card subscription in one call. Used by the in-card
 * listeners before they rebuild, and by `dismissCompareCard`. Centralising
 * the unsubscribe logic prevents the "forgot to release axis X" leak as new
 * constraint axes are added.
 */
function releaseCompareSubscriptions(): void {
  if (activeCompareUnsubscribe) {
    activeCompareUnsubscribe();
    activeCompareUnsubscribe = null;
  }
  if (activeComparePriorityUnsubscribe) {
    activeComparePriorityUnsubscribe();
    activeComparePriorityUnsubscribe = null;
  }
  if (activeCompareBandUnsubscribe) {
    activeCompareBandUnsubscribe();
    activeCompareBandUnsubscribe = null;
  }
  if (activeCompareBucketUnsubscribe) {
    activeCompareBucketUnsubscribe();
    activeCompareBucketUnsubscribe = null;
  }
}

// --- Per-side signal extraction --------------------------------------------
//
// Tombstone — REMOVED in Phase 2 (numeric-score removal):
//   - `FacetScore` type  (price/safety/vibe/commute → 0–10 + note)
//   - `Comparison` type  (per-facet object + `total`)
//   - `scoreAll(v)`      (composite 0–40)
//   - `scorePrice/Safety/Vibe/Commute(v)` (per-facet scorers)
//   - `levelBase(v)`     (verdict→base-score helper for the scorers)
//   - `reasonText/reasonMentions/clamp01to10` (helpers consumed only by the
//     scorers above)
//   - `topFacet/bottomFacet/dominantFacet` (facet-ranking helpers)
//   - `bestForPhrase/tradeoffPhrase/strengthPhrase`
//     (facet-keyed copy generators — replaced by tag-derived defaults below)
//   - `facetCell` (per-facet table cell renderer)
//   - `confidenceChipHTML/deriveConfidenceBand`
//     (confidenceScore→High/Medium/Low chip)
//
// What replaces them, structurally:
//   `signalCardFor(v, city)` returns the four kept signals (verdict colour,
//   reasons, school density, safety level, tradeoff) plus a one-line tag-
//   derived "best for" descriptor. Two of these go into the side-by-side
//   columns, the winner's drives the prescriptive verdict block.
//
// Why the helpers below stayed:
//   `priceMidKUnits` powers a non-numeric trust bullet ("Lower entry for
//   families" etc.) inside `whyBullets`. `hasTag` is used by every tag-keyed
//   helper in the file, not just the deleted scorers.

/** Extract numeric midpoint (in "k" units) from a price range string. Used
 *  only by `whyBullets` to bucket the price band into a Lower/Mid/Premium
 *  qualitative bullet — no numeric value is rendered to the user. */
function priceMidKUnits(range: string): number | null {
  const matches = [...range.matchAll(/(\d+(?:\.\d+)?)\s*k/gi)].map((m) =>
    parseFloat(m[1])
  );
  if (matches.length === 0) return null;
  const lo = matches[0];
  const hi = matches[matches.length - 1];
  return (lo + hi) / 2;
}

function hasTag(v: Verdict, ...needles: string[]): boolean {
  const t = (v.tags || []).map((x) => x.toLowerCase());
  return needles.some((n) => t.includes(n.toLowerCase()));
}

/* -------------------------------------------------------------------------- */
/*  Verdict-colour ordering                                                   */
/*                                                                            */
/*  Replaces the old `sa.total > sb.total` winner test. Higher = better.      */
/*  Green > Amber > Red.                                                      */
/* -------------------------------------------------------------------------- */
function verdictRank(v: Verdict): number {
  if (v.verdict === "green") return 3;
  if (v.verdict === "amber") return 2;
  return 1; // red
}

/* -------------------------------------------------------------------------- */
/*  SignalSet — what each side surfaces in the new column layout.             */
/*                                                                            */
/*  This is the COMPLETE per-side input to the card render. No score, no      */
/*  composite, no facet vector. The four kept signals plus a one-line price   */
/*  qualifier (categorical, never a number) drive the columns; `bestFor` and  */
/*  `tradeoffNote` drive the prescriptive verdict copy.                       */
/* -------------------------------------------------------------------------- */
interface SignalSet {
  verdict: Verdict;
  schoolLevel: "high" | "medium" | "low";
  schoolCount: number;
  safety: SafetyLevel;
  safetyReady: boolean;
  tradeoff: Tradeoff | null;
  /** Short reasons taken from `verdict.reasons`, capped at 2. */
  reasons: string[];
  /** Tag-derived "best-for audience" sentence — replaces the deleted
   *  facet-keyed `bestForPhrase`. */
  bestFor: string;
  /** Tag-derived "what you give up" sentence — replaces the deleted
   *  facet-keyed `tradeoffPhrase`. Falls back to the tradeoff helper's
   *  reason text when a tradeoff case is detected. */
  tradeoffNote: string;
}

function deriveSignals(
  v: Verdict,
  city: "hyd" | "mel" | "blr" | "syd",
  mode: LensMode
): SignalSet {
  const density = getSchoolDensity(v.id);
  const safety = getSafety(v.id, city);
  const tradeoff = getTradeoff({ verdict: v, city });
  const reasons = (v.reasons || []).slice(0, 2);
  return {
    verdict: v,
    schoolLevel: density.level,
    schoolCount: density.count,
    safety: safety.level,
    safetyReady: safety.ready,
    tradeoff,
    reasons,
    bestFor: bestForFromTags(v, mode),
    tradeoffNote: tradeoffNoteFromSignals(v, tradeoff, mode),
  };
}

/**
 * Tag-derived "best for: <audience>" descriptor. Replaces the deleted
 * `bestForPhrase(winner, facet, mode)` — that helper was facet-keyed (it
 * needed to know the winning facet to pick copy) and the facet vocabulary is
 * gone. The new picker walks tags directly, in the same persona-aware order
 * the old function used, so the resulting voice is unchanged.
 */
function bestForFromTags(v: Verdict, mode: LensMode): string {
  const t = new Set((v.tags || []).map((x) => x.toLowerCase()));

  if (mode === "family") {
    if (t.has("school-catchment") || t.has("family"))
      return "families needing quiet + strong schools";
    if (t.has("leafy") || t.has("village"))
      return "parents wanting a calm, leafy pocket";
    if (t.has("growth-corridor") || t.has("estate") || t.has("new-build"))
      return "families stretching budget into a 4BR new-build";
    if (t.has("beach") || t.has("waterfront"))
      return "families who want weekend beach life";
    if (t.has("activity-centre") || t.has("retail-hub"))
      return "families who want shops + cafes close by";
    if (t.has("it-corridor"))
      return "families cutting the tech-corridor commute";
    if (t.has("transit"))
      return "families cutting the school-run + work commute";
    return "families wanting a settled, low-drama street";
  }

  if (mode === "bachelor") {
    if (t.has("nightlife") || t.has("cbd"))
      return "renters prioritising nightlife + late food";
    if (t.has("cafe-culture") || t.has("food-scene"))
      return "young pros who live by cafe + bar life";
    if (t.has("beach") || t.has("waterfront"))
      return "renters chasing weekend water + street life";
    if (t.has("heritage") || t.has("cultural"))
      return "creatives who want street character";
    if (t.has("it-corridor"))
      return "tech workers cutting daily drive time";
    if (t.has("transit"))
      return "solo renters who still need rail access";
    return "renters chasing everyday street life";
  }

  // investor
  if (t.has("growth-corridor") || t.has("estate") || t.has("new-build"))
    return "investors chasing entry price + growth-corridor upside";
  if (t.has("it-corridor"))
    return "investors capturing tech-corridor rental demand";
  if (t.has("transit") || t.has("activity-centre") || t.has("cbd"))
    return "investors capturing transit-premium rentals";
  if (t.has("school-catchment") || t.has("family"))
    return "investors wanting low-churn family tenants";
  if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene"))
    return "investors riding rental demand from lifestyle renters";
  return "investors wanting long-tenure, low-vacancy pockets";
}

/**
 * "What you give up" line — used in the prescriptive verdict block.
 * Replaces the deleted `tradeoffPhrase(winnerFacet, loserFacet, mode)`.
 *
 * Resolution order:
 *   1. If `getTradeoff()` flagged a contradiction, prefer its reason — the
 *      tradeoff module already encodes the dominant decision-blocking gap
 *      (safety > school > strong-schools-but-risky-safety).
 *   2. Otherwise, derive a tag-keyed line in the persona's voice.
 *   3. Fall back to a neutral phrase so the line is never empty.
 */
function tradeoffNoteFromSignals(
  v: Verdict,
  detected: Tradeoff | null,
  mode: LensMode
): string {
  if (detected) return detected.reason;
  const t = new Set((v.tags || []).map((x) => x.toLowerCase()));

  if (mode === "family") {
    if (t.has("nightlife")) return "more buzz nearby, less quiet after bedtime";
    if (t.has("long-commute") || t.has("no-train") || t.has("car-dependent"))
      return "settled streets, but commute + school-run stretch";
    if (t.has("growth-corridor") || t.has("new-build"))
      return "more space for the budget, less mature infrastructure";
    return "fits family life now; weekend buzz is thinner";
  }

  if (mode === "bachelor") {
    if (t.has("long-commute") || t.has("no-train") || t.has("car-dependent"))
      return "the buzz wins, the commute is the price";
    if (t.has("school-catchment") || t.has("family") || t.has("leafy"))
      return "settled and walkable, less late-night scene";
    return "fits weekend life now; rent premium is real";
  }

  // investor
  if (t.has("growth-corridor") || t.has("new-build"))
    return "cheaper entry, growth slower without rail";
  if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene"))
    return "lifestyle demand up, tenant churn can rise too";
  if (t.has("school-catchment") || t.has("family"))
    return "low-churn tenants, weaker short-stay demand";
  return "yield reads cleanly; growth depends on the cycle";
}

// --- Rendering -------------------------------------------------------------

/* -------------------------------------------------------------------------- */
/*  Mobile stylesheet — idempotent injection.                                 */
/*  The centered 560px card overflows and clips on phones, especially on iOS  */
/*  Chrome/Safari where the collapsing URL bar makes vh-based heights         */
/*  oversize. Convert it to a full-width bottom-sheet that scrolls            */
/*  internally, respecting safe-area and the dynamic viewport.                */
/* -------------------------------------------------------------------------- */
function ensureCompareMobileStyles(): void {
  if (document.getElementById("hyperlocal-compare-mq")) return;
  const s = document.createElement("style");
  s.id = "hyperlocal-compare-mq";
  s.textContent = `
    @media (max-width: 720px), (max-height: 500px) {
      #${CARD_ID} {
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        top: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        /* dvh fallback handles iOS Chrome URL-bar collapse; the calc()
         * leaves 96px above the card so the top pills remain reachable. */
        max-height: 72vh !important;
        max-height: min(72dvh, calc(100dvh - 96px)) !important;
        overflow-y: auto !important;
        overscroll-behavior: contain !important;
        -webkit-overflow-scrolling: touch !important;
        border-radius: var(--r-card) var(--r-card) 0 0 !important;
        padding: 14px 16px calc(16px + env(safe-area-inset-bottom, 0px)) !important;
        z-index: 2200 !important;
      }
      #${CARD_ID}::before {
        content: "";
        display: block;
        width: 36px;
        height: 4px;
        background: rgba(255,255,255,0.18);
        border-radius: 999px;
        margin: -4px auto 10px;
      }
    }
  `;
  document.head.appendChild(s);
}

export function dismissCompareCard(): void {
  const existing = document.getElementById(CARD_ID);
  if (existing) existing.remove();
  releaseCompareSubscriptions();
}

export function showCompareCard(
  a: Verdict,
  b: Verdict,
  opts: {
    onClose?: () => void;
    /** Full dataset for the active city; enables "Better alternative nearby". */
    dataset?: Verdict[];
  } = {}
): void {
  dismissCompareCard();
  ensureCompareMobileStyles();

  // Compare card is invokable from Exploration mode (the Compare pill is
  // always visible). The card's phrasing is persona-specific so we
  // narrow lens=none to a sensible default for display — family reads as
  // the most neutral persona for a comparison view.
  const rawMode = getMode();
  const mode: PersonaMode = rawMode === "none" ? "family" : rawMode;
  const modeAccent = PERSONA_ACCENT[mode];
  const priority = getPriority();
  const priorityAccent = getPriorityAccent(priority);
  // Budget × School intersection — only live under School priority. The fit
  // chip + band-tinted accent + fit-aware decision anchor all key off of
  // `fitA` / `fitB` being non-null. Outside of School priority, both are
  // null and the card reads exactly as before.
  const band = getBudgetBand();
  const bandAccent = getBudgetBandAccent(band);
  const fitA = priority === "school" ? classifyFit(a, band) : null;
  const fitB = priority === "school" ? classifyFit(b, band) : null;

  // Per-side signal sets. The card has no concept of a per-facet score
  // anymore — everything below renders off these two `SignalSet` objects.
  const cityA = deriveCityFromVerdict(a);
  const cityB = deriveCityFromVerdict(b);
  const sa = deriveSignals(a, cityA, mode);
  const sb = deriveSignals(b, cityB, mode);

  // Winner determination — verdict-colour ordering replaces the old
  // composite-score comparison. Green > Amber > Red. When both sides match
  // colour, the card surfaces a tie ("Too close to call") so a tag-derived
  // tie-break can pick a lean without faking precision the colours don't
  // give us.
  const rankA = verdictRank(a);
  const rankB = verdictRank(b);
  const winner: "a" | "b" | "tie" =
    rankA === rankB ? "tie" : rankA > rankB ? "a" : "b";

  const card = document.createElement("div");
  card.id = CARD_ID;
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-label", `Compare ${a.name} vs ${b.name}`);
  card.style.cssText = `
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    width: min(94vw, 560px);
    background: #1a1a1a;
    color: #f1f1f1;
    border-radius: var(--r-card);
    padding: 18px 20px 16px 20px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06);
    font: 13px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    z-index: 2000;
    border-top: 3px solid ${modeAccent.badge};
  `;

  // Header: "A vs B" title, lens badge, and close. The lens badge mirrors
  // verdict-card so both surfaces are visibly anchored to the same global
  // mode — when the user changes the lens up top, both badges flip in sync.
  const header = document.createElement("div");
  header.style.cssText =
    "display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;flex-wrap:wrap;";

  const titleGroup = document.createElement("div");
  titleGroup.style.cssText =
    "display:flex;align-items:center;gap:8px;flex-wrap:wrap;";

  const title = document.createElement("div");
  title.style.cssText =
    "font-size:15px;font-weight:600;color:#fff;letter-spacing:0.01em;";
  title.textContent = `${a.name}  vs  ${b.name}`;

  const modeBadge = document.createElement("span");
  modeBadge.textContent = `Lens · ${getModeLabel(mode)}`;
  modeBadge.style.cssText = `
    background: ${modeAccent.badge};
    color: ${modeAccent.badgeFg};
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 4px 9px;
    border-radius: 999px;
    text-transform: uppercase;
  `;

  titleGroup.appendChild(title);
  titleGroup.appendChild(modeBadge);

  // Priority badge — mirrors verdict-card. Only shown when an active
  // constraint is set, so the default (no priority) stays quiet.
  if (priority !== "none") {
    const priorityBadge = document.createElement("span");
    priorityBadge.textContent = `Priority · ${getPriorityLabel(priority)}`;
    priorityBadge.style.cssText = `
      background: ${priorityAccent.badge};
      color: ${priorityAccent.badgeFg};
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 4px 9px;
      border-radius: 999px;
      text-transform: uppercase;
    `;
    titleGroup.appendChild(priorityBadge);
  }

  // Fit chip — Budget × School intersection. Rendered in-line with the
  // existing badges so the classification (Good fit / Stretch / Weak fit)
  // reads next to its context, not in a new section. Tracks the *winner*
  // under a clear recommendation; under a tie the chip shows the side whose
  // fit is stronger, with the winning-side's name suffixed for clarity.
  // Hidden outside School priority + a budget-band selection so the default
  // UI stays compact.
  const winnerFit: "good" | "stretch" | "weak" | null =
    winner === "a" ? fitA : winner === "b" ? fitB : pickTieFit(fitA, fitB);
  const winnerFitSide: "a" | "b" | null =
    winner === "a"
      ? fitA
        ? "a"
        : null
      : winner === "b"
      ? fitB
        ? "b"
        : null
      : pickTieFitSide(fitA, fitB);
  if (winnerFit && winnerFitSide) {
    const fitChip = document.createElement("span");
    const sideName = winnerFitSide === "a" ? a.name : b.name;
    fitChip.textContent = `${fitLabel(winnerFit)} · ${getBudgetBandLabel(band)} · ${sideName}`;
    fitChip.style.cssText = `
      background: ${bandAccent.badge};
      color: ${bandAccent.badgeFg};
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 4px 9px;
      border-radius: 999px;
      text-transform: uppercase;
    `;
    titleGroup.appendChild(fitChip);
  }

  const close = document.createElement("button");
  close.setAttribute("aria-label", "Close compare");
  close.textContent = "×";
  // Absolute-positioned against the card — same contract as verdict-card.
  // Card is position:fixed → its own positioning context. Anchored to the
  // card's top-right so the X always sits on the card's top edge regardless
  // of whether title + lens/priority chips wrap to a second line on narrow
  // screens. z-index:3 keeps it above wrapped chips and the rows below.
  close.style.cssText = `
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 3;
    background: transparent;
    border: 0;
    color: #888;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 6px;
  `;
  close.addEventListener("click", () => {
    dismissCompareCard();
    opts.onClose?.();
  });
  close.addEventListener("mouseenter", () => (close.style.color = "#fff"));
  close.addEventListener("mouseleave", () => (close.style.color = "#888"));

  // Reserve space on the right of the header row so wrapped chips never flow
  // under the absolute-positioned close X. Only a padding hint; no layout
  // change.
  header.style.paddingRight = "32px";
  header.appendChild(titleGroup);

  // Column headers — two columns now (the old layout was Facet | A | B; with
  // facet rows gone the leading column has no purpose). Highlight colour on
  // the winning side stays so the eye still tracks the recommended pick.
  const colHead = document.createElement("div");
  colHead.style.cssText =
    "display:grid;grid-template-columns: 1fr 1fr;gap:10px;padding-bottom:6px;border-bottom:1px solid rgba(255,255,255,0.08);margin-bottom:6px;font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.04em;";
  const hA = document.createElement("div");
  hA.textContent = a.name;
  hA.style.color = winner === "a" ? "#FFD37E" : "#aaa";
  const hB = document.createElement("div");
  hB.textContent = b.name;
  hB.style.color = winner === "b" ? "#FFD37E" : "#aaa";
  colHead.appendChild(hA);
  colHead.appendChild(hB);

  // Per-side signal columns. Each column shows: verdict pill, top reasons
  // (max 2), school-presence line, safety line, tradeoff line (only when a
  // tradeoff is detected). No numbers, no progress bars.
  const grid = document.createElement("div");
  grid.style.cssText =
    "display:grid;grid-template-columns: 1fr 1fr;gap:10px;padding:4px 0 8px 0;";
  grid.appendChild(signalColumn(sa, winner === "a"));
  grid.appendChild(signalColumn(sb, winner === "b"));

  // Final verdict block — border + background tinted by active lens so the
  // user can feel at a glance which mode is driving the recommendation. When
  // an active constraint Priority is set, the border + tint switch to the
  // priority colour so the user reads "this verdict respects my priority"
  // before reading the copy. When Budget × School is live (winnerFit set),
  // the accent shifts again to the budget-band colour — the second axis is
  // the dominant frame at that point and the card should read that way.
  // Mode still drives the copy's persona framing.
  const finalAccent = winnerFit
    ? bandAccent.badge
    : priority === "none"
    ? modeAccent.badge
    : priorityAccent.badge;
  const final = document.createElement("div");
  final.style.cssText = `
    margin-top:8px;padding:12px 14px;border-radius:10px;
    background:${finalAccent}12;
    border:1px solid ${finalAccent}55;
    font-size:14px;color:#f1f1f1;line-height:1.55;
  `;
  const verdictHTML = document.createElement("div");
  verdictHTML.innerHTML = prescriptiveVerdict(
    a, b, sa, sb, winner, mode, priority, band, fitA, fitB
  );
  final.appendChild(verdictHTML);

  card.appendChild(header);
  card.appendChild(colHead);
  card.appendChild(grid);
  card.appendChild(final);

  // Optional "Better alternative nearby" row. We capture the suggestion's id
  // so the follow-on "Continue exploring" chips can exclude it and avoid
  // repeating the same area twice on one card.
  //
  // After numeric-score removal, `findBetterNearby` ranks by verdict colour
  // (green > amber > red) — the "stronger option" is now an option whose
  // verdict reads cleaner than the winner's, not one whose composite total
  // edges past a threshold.
  let strongerOptionId: string | null = null;
  if (winner !== "tie" && opts.dataset && opts.dataset.length > 0) {
    const winnerV = winner === "a" ? a : b;
    const suggestion = findBetterNearby(winnerV, a, b, opts.dataset);
    if (suggestion) {
      const row = buildSuggestionRow(suggestion, winnerV, opts);
      card.appendChild(row);
      strongerOptionId = suggestion.id;
    }
  }

  // "Continue exploring" — 1–2 one-tap chips that anchor on A and B and pick
  // their nearest dataset neighbour (excluding A, B, and whichever area the
  // Stronger-option row already proposed). Clicking a chip invokes
  // `showCompareCard(anchor, partner, opts)` so the chain extends indefinitely
  // without a new screen or popup.
  if (opts.dataset && opts.dataset.length > 0) {
    const exclude = new Set<string>([a.id, b.id]);
    if (strongerOptionId) exclude.add(strongerOptionId);
    const nextRow = buildNextComparisonsRow(a, b, opts.dataset, exclude, opts);
    if (nextRow) card.appendChild(nextRow);
  }

  // Close button is absolute-positioned against the card (top-right), so it
  // is appended to the card itself — not inside the header row. Appended
  // last so its z-index context sits above every earlier child.
  card.appendChild(close);

  document.body.appendChild(card);

  // Subscribe to the global lens. If the user toggles modes via the
  // top-level control while this comparison is open, re-render in place so
  // every mode-driven surface (badge colour, accent strip, final-block
  // border, prescriptive verdict copy) refreshes together.
  activeCompareUnsubscribe = subscribe((m) => {
    if (m === mode) return;
    releaseCompareSubscriptions();
    showCompareCard(a, b, opts);
  });

  // Subscribe to the global priority. Flipping Budget/School/Commute rebuilds
  // the card so Best-for, Tradeoff, priority badge, and final-block accent
  // all update together. Scoring is untouched — priority reframes the copy
  // but never changes the underlying facet scores.
  activeComparePriorityUnsubscribe = subscribePriority((p) => {
    if (p === priority) return;
    releaseCompareSubscriptions();
    showCompareCard(a, b, opts);
  });

  // Subscribe to the global budget band. Flipping Tight/Moderate/Stretch
  // while the compare card is open rebuilds it so the fit chip, accent, and
  // decision anchor all update in lockstep with the priority axis. Scoring
  // is untouched — the band only reframes the school-priority copy.
  activeCompareBandUnsubscribe = subscribeBudgetBand((newBand) => {
    if (newBand === band) return;
    releaseCompareSubscriptions();
    showCompareCard(a, b, opts);
  });
  // Bucket-level rebuild — catches within-band moves where the band
  // subscriber above no-ops (band unchanged) but the clamped-midpoint
  // price note needs to refresh.
  activeCompareBucketUnsubscribe = subscribeBudgetBucket(() => {
    releaseCompareSubscriptions();
    showCompareCard(a, b, opts);
  });

  // Esc to dismiss
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      dismissCompareCard();
      opts.onClose?.();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);
}

/**
 * Per-side signal column. Renders the four kept signals — verdict colour
 * (as a pill), reasons (max 2 bullets), school presence line, safety line,
 * and an optional tradeoff line. Replaces the old four-row facet table; one
 * column per area instead of one row per facet.
 *
 * The amber-tinted background on `isWinner` is the same affordance the old
 * `facetCell` used so the eye still finds the recommended pick at a glance.
 */
function signalColumn(s: SignalSet, isWinner: boolean): HTMLElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = `
    padding: 10px 12px;
    border-radius: 10px;
    background: ${isWinner ? "rgba(255,211,126,0.10)" : "rgba(255,255,255,0.03)"};
    border: 1px solid ${isWinner ? "rgba(255,211,126,0.35)" : "rgba(255,255,255,0.06)"};
    display: flex;
    flex-direction: column;
    gap: 6px;
  `;

  // Verdict pill — colour is the only quantitative signal we surface.
  const pill = document.createElement("div");
  const dotColour =
    s.verdict.verdict === "green"
      ? "#7CDCA8"
      : s.verdict.verdict === "amber"
      ? "#FFD37E"
      : "#E89797";
  const pillLabel =
    s.verdict.verdict === "green"
      ? "Green read"
      : s.verdict.verdict === "amber"
      ? "Amber read"
      : "Red read";
  pill.style.cssText = `
    display:inline-flex;align-items:center;gap:6px;
    font-size:11.5px;font-weight:700;letter-spacing:0.04em;
    text-transform:uppercase;color:${dotColour};
    align-self:flex-start;
  `;
  pill.innerHTML = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dotColour};"></span>${pillLabel}`;
  wrap.appendChild(pill);

  // Reasons — first two `verdict.reasons`, rendered as compact bullets.
  if (s.reasons.length > 0) {
    const list = document.createElement("ul");
    list.style.cssText = "list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:2px;";
    for (const r of s.reasons) {
      const li = document.createElement("li");
      li.style.cssText = `position:relative;padding-left:10px;font-size:12px;line-height:1.45;color:${isWinner ? "#FFD37E" : "#d8d8d8"};`;
      li.innerHTML = `<span style="position:absolute;left:0;top:8px;width:3px;height:3px;border-radius:50%;background:${isWinner ? "rgba(255,211,126,0.7)" : "rgba(255,255,255,0.45)"};"></span>${r}`;
      list.appendChild(li);
    }
    wrap.appendChild(list);
  }

  // School presence — categorical only. "Schools: High" never "5/10".
  const schoolRow = document.createElement("div");
  schoolRow.style.cssText = "font-size:11.5px;color:#bdbdbd;line-height:1.4;";
  schoolRow.innerHTML = `<span style="color:#888;">Schools:</span> <strong style="color:#e6e6e6;font-weight:600;">${densityLabel(s.schoolLevel)}</strong>`;
  wrap.appendChild(schoolRow);

  // Safety — categorical bucket from `getSafety`. Hidden until ready so we
  // never paint a fake "safe" label on a feed that hasn't loaded yet.
  if (s.safetyReady) {
    const safetyRow = document.createElement("div");
    safetyRow.style.cssText = "font-size:11.5px;color:#bdbdbd;line-height:1.4;";
    const safetyLabel =
      s.safety === "safe" ? "Low" : s.safety === "moderate" ? "Mixed" : "High";
    const safetyColour =
      s.safety === "safe" ? "#9ed8b5" : s.safety === "moderate" ? "#FFD37E" : "#E89797";
    safetyRow.innerHTML = `<span style="color:#888;">Safety concerns:</span> <strong style="color:${safetyColour};font-weight:600;">${safetyLabel}</strong>`;
    wrap.appendChild(safetyRow);
  } else {
    const safetyRow = document.createElement("div");
    safetyRow.style.cssText = "font-size:11.5px;color:#888;line-height:1.4;";
    safetyRow.innerHTML = `<span style="color:#888;">Safety concerns:</span> <em style="color:#888;">checking…</em>`;
    wrap.appendChild(safetyRow);
  }

  // Tradeoff — only rendered when a contradiction was detected. Keeps the
  // column compact for the (common) case where verdict + signals all line
  // up.
  if (s.tradeoff) {
    const tradeRow = document.createElement("div");
    tradeRow.style.cssText = "font-size:11.5px;color:#FFD37E;line-height:1.4;";
    tradeRow.innerHTML = `<span style="color:#888;">⚠ Tradeoff:</span> <span style="color:#FFD37E;">${s.tradeoff.reason}</span>`;
    wrap.appendChild(tradeRow);
  }

  return wrap;
}

// --- Prescriptive verdict --------------------------------------------------
//
// Tombstone — REMOVED in Phase 2 (numeric-score removal):
//
//   - `Facet` type ("price" | "safety" | "vibe" | "commute")
//   - `strengthPhrase(facet)` — facet-keyed "why you'd pick" copy
//   - `bestForPhrase(winner, facet, mode)` — facet-keyed audience copy
//   - `tradeoffPhrase(winnerFacet, loserFacet, mode)` — pair-keyed tradeoff
//     copy
//   - `dominantFacet(s, other)` / `topFacet(s)` / `bottomFacet(s)`
//   - `ConfidenceBand` type + `deriveConfidenceBand(a, b)` +
//     `confidenceChipHTML(band)` (per-comparison High/Medium/Low chip — also
//     a number-derived label, removed for the same reason).
//
// The prescriptive verdict block below now drives off `SignalSet.bestFor`
// and `SignalSet.tradeoffNote`, both of which are tag-derived. The
// per-side `winner.confidenceScore` field is left untouched in the data
// schema; only the chip that surfaced it is gone.
//
// What replaces them:
//   - "Best for" line:   pulled from `SignalSet.bestFor` (tag-keyed)
//   - "Tradeoff" line:   pulled from `SignalSet.tradeoffNote` (tradeoff-detector
//                        first, tag-keyed fallback)
//   - "If you had to pick today" line: same `decisionAnchorPhrase` /
//     `tieDecisionAnchor` / `tieDecisionAnchorByPriority` paths, but the
//     tie-break helpers no longer take facet scores — see their refactored
//     bodies below for the verdict-colour + tag substitutes.

/**
 * Build up to 2 short "Why this verdict" bullets from existing Verdict fields
 * (priceRange, tags, reasons). Each bullet is ≤6 words and specific — no
 * "market patterns", "local signals", or "observations" filler. For a tie,
 * derive neutral comparability bullets. No new data sources — pure mapping.
 */
function whyBullets(
  winner: Verdict | null,
  a: Verdict,
  b: Verdict,
  mode: LensMode,
  priority: Priority
): string[] {
  if (!winner) {
    // Tie — 2 tight comparability bullets, mode-aware framing.
    // Under School priority, swap bullet 2 for the two suburbs' school hooks
    // so the "tie" read still carries concrete school signal — the user's
    // non-negotiable is surfaced, not washed out.
    if (priority === "school") {
      const aHook = schoolFactBullet(a);
      const bHook = schoolFactBullet(b);
      return ["Catchment signals differ", `${a.name}: ${aHook} · ${b.name}: ${bHook}`];
    }
    if (mode === "bachelor") return ["Rent bands sit close", "Street life similar"];
    if (mode === "investor") return ["Yield bands close", "Tenant depth similar"];
    return ["Price bands sit close", "Family-fit patterns match"];
  }
  const tags = new Set((winner.tags || []).map((t) => t.toLowerCase()));
  const reasons = (winner.reasons || []).join(" ").toLowerCase();
  const out: string[] = [];
  const mid = priceMidKUnits(winner.priceRange);

  // Bullet 1 — price/yield framing, mode-aware.
  if (mode === "investor") {
    if (mid != null && mid <= 12) out.push("Entry price supports yield");
    else if (mid != null && mid >= 22) out.push("Premium stock; yield thinner");
    else out.push("Mid-band entry + steady yield");
  } else if (mode === "bachelor") {
    if (mid != null && mid <= 12) out.push("Rent stretches further here");
    else if (mid != null && mid >= 22) out.push("Rent premium for buzz");
    else out.push("Mid-band rent for the area");
  } else {
    // family (default)
    if (mid != null && mid <= 12) out.push("Lower entry for families");
    else if (mid != null && mid >= 22) out.push("Premium family-home band");
    else out.push("Mid-band family-home pricing");
  }

  // Bullet 2 — strongest character signal, mode-aware selection.
  // School-priority override: when the user has pinned School as their
  // non-negotiable, bullet 2 must be the concrete school hook for this
  // suburb (from the school-facts catalog). Generic character bullets would
  // bury what the user actually asked for. Pass-through for non-school
  // priorities so the existing mode-aware selection still drives.
  if (priority === "school") {
    out.push(schoolFactBullet(winner));
  } else if (mode === "family") {
    // Rail-access probe is a hard gate on "Car-first family pocket" — a
    // suburb with a working train station should never be painted as
    // car-first even when it's tagged `long-commute`.
    const rail = hasRailAccess(winner);
    if (tags.has("family") || tags.has("school-catchment") || tags.has("leafy") || tags.has("village"))
      out.push("Zoned to strong public schools");
    else if (rail && (tags.has("transit") || tags.has("activity-centre") || tags.has("it-corridor")))
      out.push("Short school-run + commute");
    else if (!rail && (tags.has("no-train") || tags.has("car-dependent") || tags.has("long-commute")))
      out.push("Car-first family pocket");
    else if (rail && tags.has("long-commute"))
      out.push("Rail runs, but school-run is long");
    else if (reasons.includes("safety") || reasons.includes("mixed"))
      out.push("Mixed safety by pocket");
    else if (tags.has("beach") || tags.has("waterfront"))
      out.push("Weekend life at the water");
    else
      out.push("Settled residential feel");
  } else if (mode === "bachelor") {
    const rail = hasRailAccess(winner);
    if (tags.has("nightlife") || tags.has("cbd"))
      out.push("Nightlife at the doorstep");
    else if (tags.has("cafe-culture") || tags.has("food-scene") || tags.has("bars"))
      out.push("Cafe + bar scene is real");
    else if (rail)
      out.push("Car-free commute works");
    else if (tags.has("beach") || tags.has("waterfront"))
      out.push("Weekend water + scene");
    else if (!rail && (tags.has("long-commute") || tags.has("no-train") || tags.has("car-dependent")))
      out.push("Car-first if you live here");
    else
      out.push("Everyday street life");
  } else {
    // investor
    if (tags.has("growth-corridor") || tags.has("new-build") || tags.has("estate"))
      out.push("Growth-corridor upside");
    else if (tags.has("school-catchment") || tags.has("family") || tags.has("leafy"))
      out.push("Low-churn family tenants");
    else if (tags.has("transit") || tags.has("activity-centre") || tags.has("it-corridor"))
      out.push("Transit-premium rental depth");
    else if (tags.has("nightlife") || tags.has("cafe-culture") || tags.has("food-scene"))
      out.push("Lifestyle rental demand");
    else if (reasons.includes("safety") || reasons.includes("mixed"))
      out.push("Vacancy risk in edges");
    else
      out.push("Steady tenant reputation");
  }

  return out.slice(0, 2);
}

/**
 * Render the trust block (why bullets + source signals + uncertainty) as
 * inline HTML inside the existing final container. Not a new UI element —
 * just three additional text rows.
 */
function trustBlockHTML(
  winner: Verdict | null,
  a: Verdict,
  b: Verdict,
  mode: LensMode,
  priority: Priority
): string {
  // Level 3 — supporting context. Smaller, lighter, lower contrast than the
  // verdict line and the "When NOT" / Stronger-option callouts above. Source
  // and uncertainty merged into one line to cut visual noise.
  const bullets = whyBullets(winner, a, b, mode, priority);
  const li = bullets
    .map(
      (b) => `<li style="padding:2px 0 2px 12px;position:relative;color:#a8a8a8;font-size:11.5px;line-height:1.4;"><span style="position:absolute;left:0;top:9px;width:3px;height:3px;border-radius:50%;background:rgba(255,211,126,0.6);"></span>${b}</li>`
    )
    .join("");
  return `
    <div style="margin-top:10px;padding-top:10px;border-top:1px solid rgba(255,255,255,0.06);">
      <div style="font-size:9.5px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#777;margin-bottom:4px;">Why this verdict</div>
      <ul style="list-style:none;padding:0;margin:0 0 6px 0;">${li}</ul>
      <div style="font-size:10.5px;color:#7a7a7a;line-height:1.5;">${reviewStampLine(a, b)} • varies by street</div>
    </div>
  `;
}

// `freshnessLineCompare` REMOVED — it stamped `new Date()` (the wall clock)
// onto every card, so the line read "Recent signal · … · <current month>"
// forever regardless of when the data was actually reviewed. Time comes
// from data: `reviewStampLine` below replaces it on both card surfaces.

const STAMP_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Data-derived review line shared by verdict-card and compare-card (it
 * lives here because verdict-card → compare-card is the cycle-free import
 * direction; compare-state already imports this module).
 *
 * Dates come from the data, never the wall clock: `PRICING_REFRESHED_AT`
 * is the monthly calibration stamp written by scripts/refresh-data.mjs
 * into pricing-override.json; `priceData.year` is the snapshot year of
 * the researched medians. A missing/unparseable stamp is said plainly as
 * unrecorded — the same rule freshness.ts enforces for feeds. UTC month,
 * deliberately: deterministic across devices at month granularity.
 */
export function reviewStampLine(...verdicts: Verdict[]): string {
  const ts = Date.parse(PRICING_REFRESHED_AT);
  const reviewed = Number.isFinite(ts)
    ? `Verdict reviewed ${STAMP_MONTHS[new Date(ts).getUTCMonth()]} ${new Date(ts).getUTCFullYear()}`
    : "review date unrecorded";
  const years = verdicts
    .map((v) => v.priceData?.year)
    .filter((y): y is number => typeof y === "number");
  if (years.length === 0) return reviewed;
  const lo = Math.min(...years);
  const hi = Math.max(...years);
  return `${reviewed} · prices calibrated ${lo === hi ? lo : `${lo}–${hi}`}`;
}

function prescriptiveVerdict(
  a: Verdict,
  b: Verdict,
  sa: SignalSet,
  sb: SignalSet,
  winner: "a" | "b" | "tie",
  mode: LensMode,
  priority: Priority,
  band: import("./budget-band").BudgetBand,
  fitA: "good" | "stretch" | "weak" | null,
  fitB: "good" | "stretch" | "weak" | null
): string {
  // No more confidence chip — the per-comparison High/Medium/Low band was
  // derived from `Verdict.confidenceScore`, a numeric input the user judged
  // untrustworthy. The verdict-colour pills in each side's column already
  // carry the strongest qualitative confidence signal we have.
  if (winner === "tie") {
    const trust = trustBlockHTML(null, a, b, mode, priority);
    // For a true tie (matching verdict colours), break the deadlock with a
    // tag-derived default so the user isn't left holding two equal options.
    // When a constraint Priority is active, it takes precedence — the
    // deadlock is broken on what the user told us is non-negotiable today.
    // When Budget × School is live, the fit axis wins outright: the side
    // with the stronger fit gets the lean and the closing anchor is the
    // fit-aware phrase.
    let tieAnchor: string;
    const tieFit = pickTieFit(fitA, fitB);
    const tieFitSide = pickTieFitSide(fitA, fitB);
    if (tieFit && tieFitSide) {
      const pick = tieFitSide === "a" ? a : b;
      tieAnchor = `lean <strong>${pick.name}</strong> — ${fitDecisionAnchor(tieFit)}.`;
    } else if (priority === "none") {
      tieAnchor = tieDecisionAnchor(a, b, sa, sb, mode);
    } else {
      tieAnchor = tieDecisionAnchorByPriority(a, b, sa, sb, priority);
    }
    // Hierarchy: L1 "Too close to call." · L2/L3 symmetric · L4 anchor · trust.
    return `
      <div style="font-size:16px;font-weight:700;color:#FFD37E;line-height:1.35;margin-bottom:6px;">Too close to call.</div>
      <div style="font-size:13px;font-weight:500;color:#c4c4c4;line-height:1.55;">Choose <strong style="color:#e8e8e8;">${a.name}</strong> for <em>${sa.bestFor}</em>.</div>
      <div style="font-size:13px;font-weight:500;color:#c4c4c4;line-height:1.55;margin-bottom:6px;">Choose <strong style="color:#e8e8e8;">${b.name}</strong> for <em>${sb.bestFor}</em>.</div>
      <div style="font-size:13px;font-weight:700;color:#fff;line-height:1.45;padding-top:6px;border-top:1px dashed rgba(255,255,255,0.10);"><span style="color:#FFD37E;">If you had to pick today,</span> ${tieAnchor}</div>
      ${trust}
    `;
  }
  const W = winner === "a" ? a : b;
  const sW = winner === "a" ? sa : sb;
  const wFit = winner === "a" ? fitA : fitB;

  const trust = trustBlockHTML(W, a, b, mode, priority);
  // Lens-baseline copy now comes from the per-side SignalSet — both lines
  // are tag-derived (`bestFor`) or tradeoff-derived (`tradeoffNote`), never
  // from a facet-vector lookup.
  const lensBest = sW.bestFor;
  const lensTrade = sW.tradeoffNote;
  // Constraint-aware override. Pass-through when priority="none" so the
  // existing lens copy stays the default. When a priority is active, the
  // Best-for + Tradeoff lines reframe around that constraint. When School
  // priority + a budget band are both active, constrainBestFor/Tradeoff
  // further swap in the Good-fit / Stretch / Weak-fit copy — the first true
  // constraint-intersection output.
  const best = constrainBestFor(W, mode, priority, lensBest, band);
  const trade = constrainTradeoff(W, mode, priority, lensTrade, band);
  // Decision anchor copy: under Budget × School intersection, the closing
  // line is the fit-aware anchor ("schools + your budget line up…") rather
  // than the lens-only verdict anchor. Lens anchor stays the default when
  // no fit is in play so non-School + no-band flows are untouched.
  const anchor = wFit
    ? fitDecisionAnchor(wFit)
    : decisionAnchorPhrase(W, mode);

  // Hierarchy:
  //   L1 — decisive recommendation: "Pick X for most cases."
  //   L2 — "Best for: <mode-aware audience>"
  //   L3 — "Tradeoff: <mode-aware cost of the pick>"
  //   L4 — decision anchor: "If you had to choose today, go with X — <one-phrase reason>"
  //   L5 — trust block (muted, subordinate)
  return `
    <div style="font-size:16px;font-weight:700;color:#fff;line-height:1.35;margin-bottom:8px;">Pick <strong>${W.name}</strong> for most cases.</div>
    <div style="font-size:13px;font-weight:500;color:#e2e2e2;line-height:1.5;margin-bottom:4px;"><span style="color:#FFD37E;font-weight:700;">Best for:</span> ${best}.</div>
    <div style="font-size:13px;font-weight:500;color:#c4c4c4;line-height:1.5;margin-bottom:6px;"><span style="color:#9aa0a6;font-weight:700;">Tradeoff:</span> ${trade}.</div>
    <div style="font-size:13px;font-weight:700;color:#fff;line-height:1.45;padding-top:6px;border-top:1px dashed rgba(255,255,255,0.10);"><span style="color:#FFD37E;">If you had to choose today,</span> go with <strong>${W.name}</strong> — ${anchor}.</div>
    ${trust}
  `;
}

/**
 * Pick the stronger of two fits for tie-break purposes. Order: good > stretch
 * > weak. Returns null if both sides have no fit (fit axis isn't in play).
 * Ties on fit fall back to side A so the lean is never empty.
 */
function pickTieFit(
  fa: "good" | "stretch" | "weak" | null,
  fb: "good" | "stretch" | "weak" | null
): "good" | "stretch" | "weak" | null {
  if (!fa && !fb) return null;
  const rank = { good: 3, stretch: 2, weak: 1 } as const;
  const ra = fa ? rank[fa] : 0;
  const rb = fb ? rank[fb] : 0;
  return ra >= rb ? fa : fb;
}

function pickTieFitSide(
  fa: "good" | "stretch" | "weak" | null,
  fb: "good" | "stretch" | "weak" | null
): "a" | "b" | null {
  if (!fa && !fb) return null;
  const rank = { good: 3, stretch: 2, weak: 1 } as const;
  const ra = fa ? rank[fa] : 0;
  const rb = fb ? rank[fb] : 0;
  return ra >= rb ? "a" : "b";
}

/**
 * Closing one-phrase reason for the winning pick. Mode-first so the anchor
 * reads as a recommendation the persona would give themselves — not a
 * recap of the facet chart above.
 */
function decisionAnchorPhrase(winner: Verdict, mode: LensMode): string {
  const tags = new Set((winner.tags || []).map((t) => t.toLowerCase()));
  if (mode === "investor") {
    if (tags.has("growth-corridor") || tags.has("new-build") || tags.has("estate"))
      return "cheaper entry with real upside";
    if (tags.has("it-corridor")) return "tech-corridor demand is already there";
    if (tags.has("transit") || tags.has("activity-centre") || tags.has("tram-corridor"))
      return "transit-premium rental depth";
    if (tags.has("school-catchment") || tags.has("family") || tags.has("leafy"))
      return "low-churn tenants and steady rent";
    if (tags.has("nightlife") || tags.has("cafe-culture") || tags.has("food-scene"))
      return "lifestyle-renter demand is strong";
    return "the risk profile reads cleanest";
  }
  if (mode === "bachelor") {
    if (tags.has("nightlife") || tags.has("cbd") || tags.has("bars"))
      return "the nightlife pays the rent premium";
    if (tags.has("cafe-culture") || tags.has("food-scene"))
      return "cafe + food scene is at the doorstep";
    if (tags.has("tram-corridor") || tags.has("metro-tunnel") || tags.has("transit"))
      return "tram/train makes car-free work";
    if (tags.has("it-corridor")) return "short commute, real street life";
    if (tags.has("beach") || tags.has("waterfront"))
      return "weekend water + summer scene";
    return "the weekend feel is stronger";
  }
  // family (default)
  if (tags.has("school-catchment") || tags.has("family") || tags.has("leafy") || tags.has("village"))
    return "schools + quiet streets line up";
  if (tags.has("beach") || tags.has("waterfront"))
    return "family weekends sort themselves out";
  if (tags.has("it-corridor")) return "work + school fit in one suburb";
  if (tags.has("growth-corridor") || tags.has("new-build") || tags.has("estate"))
    return "new-home budget goes further";
  return "the family-fit reads cleaner";
}

/**
 * Break the tie with a mode-anchored default. We still state the reason so
 * the user understands why we chose the side we did — no coin-flip feel.
 *
 * Refactored after numeric-score removal: lean is computed from categorical
 * signals (safety bucket, school density) and tag presence rather than facet
 * scores. The voice is unchanged from the old version because both signals
 * are coarse-but-honest stand-ins for what the deleted facet scores measured.
 */
function tieDecisionAnchor(
  a: Verdict,
  b: Verdict,
  sa: SignalSet,
  sb: SignalSet,
  mode: LensMode
): string {
  // Family — lean to the lower safety bucket (safer); fall back to school
  // density (more schools nearby means a stronger school-run pocket).
  if (mode === "family") {
    const pick = safetyBucketRank(sa.safety) <= safetyBucketRank(sb.safety)
      ? safetyBucketRank(sa.safety) === safetyBucketRank(sb.safety)
        ? schoolDensityRank(sa.schoolLevel) >= schoolDensityRank(sb.schoolLevel) ? a : b
        : a
      : b;
    return `lean <strong>${pick.name}</strong> — safer school-run feel.`;
  }
  // Bachelor — lean to the side with stronger lifestyle tag presence.
  if (mode === "bachelor") {
    const pick = vibeTagWeight(a) >= vibeTagWeight(b) ? a : b;
    return `lean <strong>${pick.name}</strong> — better weekend feel.`;
  }
  // Investor — lean to the cheaper-entry side (lower priceMidKUnits).
  const midA = priceMidKUnits(a.priceRange);
  const midB = priceMidKUnits(b.priceRange);
  const pick =
    midA == null || midB == null
      ? a
      : midA <= midB
      ? a
      : b;
  return `lean <strong>${pick.name}</strong> — cheaper entry, same story.`;
}

/* -------------------------------------------------------------------------- */
/*  Tie-break helpers — categorical/tag substitutes for the deleted facet     */
/*  scores. Lower-rank "safety" = safer. Higher-rank "school density" = more  */
/*  schools. `vibeTagWeight` mirrors the old `scoreVibe` tag list, but does   */
/*  not produce a 0–10 number — it just compares two areas to break a tie.    */
/* -------------------------------------------------------------------------- */
function safetyBucketRank(level: SafetyLevel): number {
  if (level === "safe") return 1;
  if (level === "moderate") return 2;
  return 3; // risky
}
function schoolDensityRank(level: "high" | "medium" | "low"): number {
  if (level === "high") return 3;
  if (level === "medium") return 2;
  return 1;
}
const _VIBE_TAGS: ReadonlyArray<string> = [
  "cafe-culture", "cultural", "creative", "cbd", "beach", "waterfront",
  "nightlife", "food-scene", "startup", "music", "market", "activity-centre",
  "retail-hub", "village", "warehouse", "heritage", "premium-inner",
  "tram-corridor", "bike",
];
function vibeTagWeight(v: Verdict): number {
  let n = 0;
  for (const t of _VIBE_TAGS) if (hasTag(v, t)) n += 1;
  return n;
}

/**
 * Break a tie using the active constraint Priority instead of the Lens.
 * When the user has pinned what's non-negotiable today (School / Commute /
 * Lifestyle), the deadlock should be broken on THAT axis, not on persona
 * bias.
 *
 * Refactored after numeric-score removal: the priority axes that used to
 * read facet scores now read categorical/tag substitutes — the school
 * density bucket, the safety bucket, and the vibe-tag count. The voice of
 * the closing copy is unchanged.
 */
function tieDecisionAnchorByPriority(
  a: Verdict,
  b: Verdict,
  sa: SignalSet,
  sb: SignalSet,
  priority: Priority
): string {
  if (priority === "commute") {
    // Commute = transit-tag presence; the deleted `scoreCommute` mainly
    // hinged on these tags anyway, so the lean is the same in practice.
    const pick = commuteTagWeight(a) >= commuteTagWeight(b) ? a : b;
    return `lean <strong>${pick.name}</strong> — commute is the easier one.`;
  }
  if (priority === "school") {
    // Compare the school-density bucket directly (the kept categorical
    // signal); fall back to the safety bucket on a tie, since a settled
    // pocket tracks with school-run viability.
    const drA = schoolDensityRank(sa.schoolLevel);
    const drB = schoolDensityRank(sb.schoolLevel);
    const pick =
      drA === drB
        ? safetyBucketRank(sa.safety) <= safetyBucketRank(sb.safety) ? a : b
        : drA > drB
        ? a
        : b;
    return `lean <strong>${pick.name}</strong> — stronger public-school zone signal.`;
  }
  if (priority === "lifestyle") {
    // Lifestyle = vibe-tag count; fall back to safety when vibe-tag count
    // is level, so a settled walkable pocket wins over an equally-vibey
    // but noisier one.
    const va = vibeTagWeight(a);
    const vb = vibeTagWeight(b);
    const pick =
      va === vb
        ? safetyBucketRank(sa.safety) <= safetyBucketRank(sb.safety) ? a : b
        : va > vb
        ? a
        : b;
    return `lean <strong>${pick.name}</strong> — stronger lifestyle + street scene.`;
  }
  // Should never fire (priority !== "none" by contract), but keep a safe
  // default — pick the cheaper-entry side.
  const midA = priceMidKUnits(a.priceRange);
  const midB = priceMidKUnits(b.priceRange);
  const pick =
    midA == null || midB == null
      ? a
      : midA <= midB
      ? a
      : b;
  return `lean <strong>${pick.name}</strong> — slight edge on cost.`;
}

/* `commuteTagWeight` mirrors the deleted `scoreCommute` tag list. Categorical
 *  tie-break only, never used as a number anywhere user-facing. */
const _COMMUTE_POSITIVE: ReadonlyArray<string> = [
  "transit", "metro-tunnel", "tram-corridor", "activity-centre",
  "it-corridor", "inner-city", "walkable",
];
const _COMMUTE_NEGATIVE: ReadonlyArray<string> = [
  "no-train", "long-commute", "infra-lag", "car-dependent",
  "tram-only", "single-line", "end-of-line", "bus-only",
];
function commuteTagWeight(v: Verdict): number {
  let n = 0;
  for (const t of _COMMUTE_POSITIVE) if (hasTag(v, t)) n += 1;
  for (const t of _COMMUTE_NEGATIVE) if (hasTag(v, t)) n -= 1;
  return n;
}

// --- Nearby suggestion -----------------------------------------------------

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * "Stronger option nearby" search — ranks by verdict colour now that the
 * composite total is gone.
 *
 * Old contract: `total > winnerTotal + 0.3` for the strictly-better pass.
 * New contract: `verdictRank(v) > verdictRank(winner)` — a green within 5 km
 * of an amber winner is a strict improvement; a same-colour candidate is
 * not. The fallback pass returns the highest-ranked candidate within 8 km
 * regardless of comparison.
 */
function findBetterNearby(
  winner: Verdict,
  a: Verdict,
  b: Verdict,
  dataset: Verdict[]
): Verdict | null {
  const [wLng, wLat] = winner.coord;
  const wRank = verdictRank(winner);
  const scored = dataset
    .filter((v) => v.id !== a.id && v.id !== b.id)
    .map((v) => ({
      v,
      km: haversineKm(wLat, wLng, v.coord[1], v.coord[0]),
      rank: verdictRank(v),
    }));

  // First pass: strictly better candidates within 5 km of the winner.
  const nearby = scored
    .filter((x) => x.km <= 5 && x.rank > wRank)
    .sort((x, y) => y.rank - x.rank);
  if (nearby[0]) return nearby[0].v;

  // Fallback: best-rank candidate within 8 km. Ties on rank fall through to
  // the closest one, so the user gets a geographically natural follow-up.
  const wider = scored
    .filter((x) => x.km <= 8)
    .sort((x, y) => y.rank - x.rank || x.km - y.km);
  return wider[0]?.v ?? null;
}

function buildSuggestionRow(
  suggestion: Verdict,
  winner: Verdict,
  opts: { onClose?: () => void; dataset?: Verdict[] }
): HTMLElement {
  const row = document.createElement("div");
  row.style.cssText = `
    margin-top:10px;padding:10px 12px;border-radius:10px;
    background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);
    display:flex;align-items:center;justify-content:space-between;gap:12px;
    font-size:13px;color:#e6e6e6;
  `;

  const label = document.createElement("div");
  label.style.cssText = "display:flex;flex-direction:column;gap:2px;";
  const top = document.createElement("div");
  top.style.cssText = "color:#FFD37E;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;";
  top.textContent = "Stronger option nearby";
  const urgency = document.createElement("div");
  urgency.style.cssText = "color:#cfcfcf;font-size:12px;font-style:italic;margin-top:1px;";
  urgency.textContent = "A better pick than both.";
  const name = document.createElement("div");
  name.style.cssText = "margin-top:3px;";
  name.innerHTML = `<strong style="color:#fff;">${suggestion.name}</strong> — ${shortWhy(
    suggestion
  )}`;
  label.appendChild(top);
  label.appendChild(urgency);
  label.appendChild(name);

  const btn = document.createElement("button");
  btn.textContent = "Compare →";
  btn.style.cssText = `
    background: rgba(255,211,126,0.18);
    color: #FFD37E;
    border: 1px solid rgba(255,211,126,0.45);
    padding: 6px 12px;
    border-radius: 999px;
    font: 500 12px system-ui;
    cursor: pointer;
  `;
  btn.addEventListener("click", () => {
    // Re-open compare with winner vs suggestion. Keeps user in decision mode.
    console.log(
      "[HYPERLOCAL] compare: follow-up suggestion picked:",
      suggestion.id,
      "vs winner",
      winner.id
    );
    showCompareCard(winner, suggestion, opts);
  });
  btn.addEventListener("mouseenter", () => {
    btn.style.background = "rgba(255,211,126,0.28)";
  });
  btn.addEventListener("mouseleave", () => {
    btn.style.background = "rgba(255,211,126,0.18)";
  });

  row.appendChild(label);
  row.appendChild(btn);
  return row;
}

/**
 * One-phrase "why this nearby option might be a better pick" line, derived
 * from the area's tags. Replaces the deleted `scoreAll` + `topFacet` walk;
 * the tag-keyed branches mirror the old per-facet copy so the voice is
 * unchanged.
 */
function shortWhy(v: Verdict): string {
  const t = new Set((v.tags || []).map((x) => x.toLowerCase()));
  if (t.has("school-catchment") || t.has("family") || t.has("leafy") || t.has("village"))
    return "settled, low-drama feel";
  if (t.has("transit") || t.has("metro-tunnel") || t.has("tram-corridor") || t.has("activity-centre"))
    return "better transit access";
  if (t.has("cafe-culture") || t.has("food-scene") || t.has("nightlife") || t.has("cultural"))
    return "stronger cafe + street life";
  if (t.has("growth-corridor") || t.has("new-build") || t.has("estate"))
    return "cheaper entry on average";
  // Fall back to the price midpoint when no character tag dominates.
  const mid = priceMidKUnits(v.priceRange);
  if (mid != null && mid <= 12) return "cheaper entry on average";
  if (verdictRank(v) === 3) return "verdict reads green here";
  return "verdict reads cleaner here";
}

// --- Continue-exploring chips ---------------------------------------------

/**
 * Pick the nearest dataset neighbour to `anchor` that isn't already in
 * `exclude`. Distance-only ranking — the chip's job is to tee up a
 * geographically natural follow-up compare, not a "best alternative" claim.
 */
function pickNearbyPartner(
  anchor: Verdict,
  dataset: Verdict[],
  exclude: Set<string>,
  maxKm = 8
): Verdict | null {
  const [aLng, aLat] = anchor.coord;
  const candidates = dataset
    .filter((v) => !exclude.has(v.id))
    .map((v) => ({
      v,
      km: haversineKm(aLat, aLng, v.coord[1], v.coord[0]),
    }))
    .filter((x) => x.km > 0.05 && x.km <= maxKm)
    .sort((x, y) => x.km - y.km);
  return candidates[0]?.v ?? null;
}

/**
 * Build the "Continue exploring" row — up to 2 one-tap chips. First chip
 * anchors on B (mirroring the user's last selection), second on A. Each chip
 * launches `showCompareCard(anchor, partner, opts)` so the chain extends with
 * the same options (dataset, onClose) and never opens a new screen.
 *
 * Returns null if no eligible partners exist (very small dataset edge case).
 */
function buildNextComparisonsRow(
  a: Verdict,
  b: Verdict,
  dataset: Verdict[],
  exclude: Set<string>,
  opts: { onClose?: () => void; dataset?: Verdict[] }
): HTMLElement | null {
  // Pick partner for B first; then for A, also excluding the partner we just
  // picked so the two chips never propose the same area.
  const seen = new Set(exclude);
  const partnerB = pickNearbyPartner(b, dataset, seen);
  if (partnerB) seen.add(partnerB.id);
  const partnerA = pickNearbyPartner(a, dataset, seen);

  const chips: Array<{ anchor: Verdict; partner: Verdict }> = [];
  if (partnerB) chips.push({ anchor: b, partner: partnerB });
  if (partnerA) chips.push({ anchor: a, partner: partnerA });
  if (chips.length === 0) return null;

  const row = document.createElement("div");
  row.style.cssText = `
    margin-top:10px;padding:10px 12px;border-radius:10px;
    background:rgba(255,255,255,0.025);border:1px solid rgba(255,255,255,0.06);
    display:flex;flex-direction:column;gap:6px;
  `;

  const label = document.createElement("div");
  label.style.cssText =
    "color:#888;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;";
  label.textContent = "Continue exploring";

  const chipsWrap = document.createElement("div");
  chipsWrap.style.cssText =
    "display:flex;flex-wrap:wrap;gap:6px;";

  for (const { anchor, partner } of chips) {
    const chip = document.createElement("button");
    chip.setAttribute("type", "button");
    chip.setAttribute(
      "aria-label",
      `Compare ${anchor.name} with ${partner.name}`
    );
    chip.style.cssText = `
      background:rgba(255,211,126,0.12);
      color:#FFD37E;
      border:1px solid rgba(255,211,126,0.35);
      padding:6px 10px;border-radius:999px;
      font:500 11.5px system-ui;
      cursor:pointer;
      display:inline-flex;align-items:center;gap:6px;
      transition:background 140ms ease,border-color 140ms ease;
      max-width:100%;
    `;
    const txt = document.createElement("span");
    txt.style.cssText =
      "white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:200px;";
    txt.textContent = `${anchor.name} vs ${partner.name}`;
    const arrow = document.createElement("span");
    arrow.textContent = "→";
    arrow.style.cssText = "opacity:0.85;";
    chip.appendChild(txt);
    chip.appendChild(arrow);

    chip.addEventListener("mouseenter", () => {
      chip.style.background = "rgba(255,211,126,0.20)";
      chip.style.borderColor = "rgba(255,211,126,0.55)";
    });
    chip.addEventListener("mouseleave", () => {
      chip.style.background = "rgba(255,211,126,0.12)";
      chip.style.borderColor = "rgba(255,211,126,0.35)";
    });

    chip.addEventListener("click", () => {
      console.log(
        "[HYPERLOCAL] compare: continue-exploring chip clicked:",
        anchor.id,
        "vs",
        partner.id
      );
      // Same recursion pattern used by the Stronger-option row — passes the
      // same opts so the chain (onClose, dataset) carries forward.
      showCompareCard(anchor, partner, opts);
    });

    chipsWrap.appendChild(chip);
  }

  row.appendChild(label);
  row.appendChild(chipsWrap);
  return row;
}
